import React, { useEffect } from 'react'
import { Linking } from 'react-native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { useNavigation } from '@react-navigation/native'

import { Images } from 'APP/Themes'

import { useDevTools } from 'APP/Lib/DevTools'

import PatientActions from 'APP/Redux/PatientRedux'
import I18n from 'APP/I18n'
import { eligibilityClaimSupportUrl } from 'APP/Helpers/UrlBuilder'

import ListItem from 'APP/Converse/ListItem'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import List from 'APP/Components/List'
import LogoHeader from 'APP/Components/LogoHeader'
import ChallengedInvitationFooter from 'APP/Components/ChallengedInvitationFooter'

type EligibilityClaimScreenProps = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'eligibilityClaimScreen'
>

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  justify-content: space-between;
  flex: 1;
`

const ScrollContainer = styled(ResponsiveScrollView)`
  flex: 1;
`

export const EligibilityClaimScreen = ({ route }: EligibilityClaimScreenProps) => {
  const dispatch = useDispatch()
  const navigation = useNavigation()
  const { setToolName, setToolFunction } = useDevTools()
  const { challengedInvitationQueue, isOnboarding } = useSelector((state) => state.patient)

  const { analyticsName, code } = route?.params || {}
  const isInChallengedQueue = (challengedInvitationQueue ?? []).length > 0

  const onboardingDeferredEnrolmentComplete = () => {
    dispatch(PatientActions.onboardingDeferredEnrolmentComplete())
  }

  const handleSkipForNow = () => {
    if (isOnboarding) onboardingDeferredEnrolmentComplete()
  }

  const navigateToClaimAccessScreen = () => {
    navigation.navigate('claimAccessScreen', { code })
  }

  const resendInvite = () => {
    navigation.navigate('invitationEmail')
  }

  const navigateToFAQ = async () => {
    await Linking.openURL(eligibilityClaimSupportUrl())
  }

  useEffect(() => {
    setToolName('Access Code')
    setToolFunction(() => () => {
      navigateToClaimAccessScreen()
    })
    return () => {
      setToolFunction(null)
      setToolName(null)
    }
  }, [])

  return (
    <ScreenContainer>
      <ScrollContainer>
        <ContentContainer>
          <LogoHeader
            source={Images.logoBlue}
            title={I18n.t('EligibilityClaimScreen.title')}
            subtitle={I18n.t('EligibilityClaimScreen.subtitle')}
          />
          <List>
            <ListItem
              key="claim-access-code"
              testID="NAVIGATE_TO_CLAIM_ACCESS_SCREEN_BTN"
              title={I18n.t('EligibilityClaimScreen.options.accessCodeTitle')}
              subtitle={I18n.t('EligibilityClaimScreen.options.accessCodeSubtitle')}
              onPress={navigateToClaimAccessScreen}
            />
            <ListItem
              key="resend-email"
              testID="RESEND_EMAIL_BTN"
              title={I18n.t('EligibilityClaimScreen.options.resendEmailTitle')}
              subtitle={I18n.t('EligibilityClaimScreen.options.resendEmailSubtitle')}
              onPress={resendInvite}
            />
            <ListItem
              key="go-to-faq"
              testID="FAQ_BTN"
              title={I18n.t('EligibilityClaimScreen.options.faqTitle')}
              subtitle={I18n.t('EligibilityClaimScreen.options.faqSubtitle')}
              iconName="exit-to-app"
              onPress={navigateToFAQ}
            />
          </List>
        </ContentContainer>
      </ScrollContainer>
      {(isOnboarding || isInChallengedQueue) && (
        <ChallengedInvitationFooter analyticsName={analyticsName} onPress={handleSkipForNow} />
      )}
    </ScreenContainer>
  )
}
